import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { NgGoogleChartsComponent } from '../../components/ng-google-charts/ng-google-charts.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import BusinessRestServices from '../../services/business-rest.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';

@Component({
	standalone: true,
	selector: 'training-program-analytics-page',
	templateUrl: './training-program-analytics-page.component.html',
	imports: [CommonModule, FormsModule, RouterLink, LoaderOverlayComponent, NgGoogleChartsComponent, TranslatePipe]
})
export default class TrainingProgramAnalyticsPageComponent implements OnInit {
	private route: ActivatedRoute = inject(ActivatedRoute);
	private businessRestServices: BusinessRestServices = inject(BusinessRestServices);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);

	vm: any = undefined;
	legendItems: Array<any> = [];
	colors: Array<string> = ['#287638', '#f24a69', '#d5d2d2'];
	circumference = 144.5;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.vm.initialized = false;
		this.vm.error = false;
		this.vm.program = null;
		this.vm.kpi = {};
		this.vm.learners = [];
		this.vm.filteredLearners = [];
		this.vm.groupOptions = [];
		this.vm.statusOptions = ['all', 'in-progress', 'certified', 'not-started'];
		this.vm.filters = {
			status: 'all',
			group: 'all',
			search: ''
		};
		this.vm.close = this.close;
		this.vm.applyLearnerFilters = this.applyLearnerFilters.bind(this);
		this.vm.getAssignedLearnersCount = this.getAssignedLearnersCount.bind(this);
		this.vm.getStatusOptionLabel = this.getStatusOptionLabel.bind(this);
		this.loadAnalyticsPage();
	}

	async loadAnalyticsPage() {
		const id = Number(this.route.snapshot.paramMap.get('id'));
		if (!id) {
			this.vm.error = true;
			this.vm.initialized = true;
			return;
		}

		try {
			const [programs, kpi, learners] = await Promise.all([
				this.businessRestServices.getTrainingPrograms(),
				this.businessRestServices.getTrainingProgramKpi({ id }),
				this.businessRestServices.getTrainingProgramLearners({ id })
			]);

			this.vm.program = (programs || []).find((item) => item?.id === id) || null;
			this.vm.kpi = kpi || {};
			this.vm.learners = this.decorateLearners(learners || [], this.vm.program);
			this.vm.groupOptions = this.buildGroupOptions(this.vm.learners);
			this.applyLearnerFilters();
			this.buildChart();
		} catch (error) {
			console.log(error);
			this.vm.error = true;
		} finally {
			this.vm.initialized = true;
		}
	}

	buildChart() {
		const total = Number(this.vm.kpi?.assignedManagers || 0);
		const certified = Number(this.vm.kpi?.completed || 0);
		const notStarted = Number(this.vm.kpi?.notStarted || 0);
		const inProgress = Math.max(0, total - certified - notStarted);

		this.vm.totalEmployees = total;
		this.vm.certifiedCount = certified;
		this.vm.inProgressCount = inProgress;
		this.vm.notStartedCount = notStarted;
		this.vm.showNoContentMessage = total === 0;

		const statusLabel = this.translate('Training.Analytics.Status');
		const employeesLabel = this.translate('Training.Analytics.Employees');
		const certifiedLabel = this.translate('Training.Analytics.Completed');
		const inProgressLabel = this.translate('Training.Analytics.In_Progress');
		const notStartedLabel = this.translate('Training.Analytics.Not_Started');

		this.vm.pieChartOptions = {
			chartType: 'PieChart',
			dataTable: [
				[statusLabel, employeesLabel],
				[certifiedLabel, certified],
				[inProgressLabel, inProgress],
				[notStartedLabel, notStarted]
			],
			options: {
				pieHole: 0.62,
				colors: this.colors,
				width: 320,
				height: 260,
				legend: { position: 'none' },
				pieSliceText: total > 0 ? 'value-and-percentage' : 'none',
				tooltip: { text: 'value-and-percentage' },
				chartArea: {
					height: '86%',
					width: '90%'
				},
				backgroundColor: 'transparent'
			}
		};

		this.legendItems = [
			this.createLegendItem(certifiedLabel, certified, total, this.colors[0]),
			this.createLegendItem(inProgressLabel, inProgress, total, this.colors[1]),
			this.createLegendItem(notStartedLabel, notStarted, total, this.colors[2])
		];
	}

	decorateLearners(learners, program) {
		const totalModules = Number(program?.programVersion?.totalModules || program?.totalModules || 0);
		console.log(program);
		return (learners || []).map((learner) => {
			const statusKey = this.normalizeLearnerStatus(learner?.certificateStatus);
			const modulesCompleted = Number(learner?.modulesCompleted || 0);
			const progressPercent = totalModules > 0 ? Math.round((modulesCompleted * 100) / totalModules) : 0;
			return {
				...learner,
				statusKey,
				groupOrTag: learner?.department || program?.groupName || '-',
				modulesProgress: totalModules > 0 ? modulesCompleted + '/' + totalModules : modulesCompleted + '/0',
				avgScoreDisplay: statusKey === 'not-started' ? '-' : Math.round(Number(learner?.avgScore || 0)) + '%',
				statusLabel: this.getLearnerStatusLabel(statusKey),
				progressPercent,
				progressStrokeOffset: this.circumference - (this.circumference * progressPercent / 100),
				completedAtDisplay: learner?.completedAt ? this.angularFilterHelperService.formatDate(learner.completedAt, 'dd.MM.yyyy') : null
			};
		});
	}

	buildGroupOptions(learners) {
		const groups = (learners || [])
			.map((learner) => learner?.groupOrTag)
			.filter((group) => !!group && group !== '-')
			.filter((group, index, items) => items.indexOf(group) === index)
			.sort((left, right) => left.localeCompare(right));
		return ['all'].concat(groups);
	}

	applyLearnerFilters() {
		const filters = this.vm.filters || {};
		const searchValue = (filters.search || '').toString().trim().toLowerCase();
		this.vm.filteredLearners = (this.vm.learners || []).filter((learner) => {
			const matchesStatus = !filters.status || filters.status === 'all' || learner.statusKey === filters.status;
			const matchesGroup = !filters.group || filters.group === 'all' || learner.groupOrTag === filters.group;
			const matchesSearch = !searchValue || (learner.name || '').toString().toLowerCase().indexOf(searchValue) !== -1;
			return matchesStatus && matchesGroup && matchesSearch;
		});
	}

	getAssignedLearnersCount() {
		return Number(this.vm.totalEmployees || this.vm.learners?.length || 0);
	}

	normalizeLearnerStatus(status) {
		const normalized = (status || '').toString().toLowerCase().replace(/_/g, '-');
		if (normalized === 'passed') return 'certified';
		if (normalized === 'not passed' || normalized === 'not-passed' || normalized === 'failed') return 'in-progress';
		if (normalized === 'not started' || normalized === 'not-started') return 'not-started';
		return 'in-progress';
	}

	getLearnerStatusLabel(statusKey) {
		if (statusKey === 'certified') {
			return this.translate('Training.Analytics.Completed');
		}
		if (statusKey === 'not-started') {
			return this.translate('Training.Analytics.Not_Started');
		}
		return this.translate('Training.Analytics.In_Progress');
	}

	createLegendItem(label, value, total, color) {
		const percentage = total > 0 ? Math.round((value * 100) / total) : 0;
		return { label, value, percentage, color };
	}

	getProgramTitle() {
		return this.vm.program?.title || this.vm.program?.name || this.translate('Training.Analytics.Title');
	}

	getLearnerStatusClass(statusKey) {
		return 'training-program-analytics-page__badge--' + statusKey;
	}

	getStatusOptionLabel(statusKey) {
		if (statusKey === 'certified') return this.translate('Training.Analytics.Completed');
		if (statusKey === 'in-progress') return this.translate('Training.Analytics.In_Progress');
		if (statusKey === 'not-started') return this.translate('Training.Analytics.Not_Started');
		return this.translate('Training.Analytics.Filter_All_Statuses');
	}

	translate(key) {
		return this.angularFilterHelperService.translate(key);
	}

	close() {
		window.history.back();
	}
}
